import React, { useState, useEffect } from 'react';
import { Menu, X, ExternalLink, Facebook, Instagram, Mail, Phone } from 'lucide-react';
import { propertyData } from '../data/mock';

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { name: 'Home', href: '#hero' },
    { name: 'About', href: '#highlights' },
    { name: 'Gallery', href: '#gallery' },
    { name: 'Reviews', href: '#reviews' },
    { name: 'Location', href: '#attractions' },
    { name: 'Book Now', href: propertyData.airbnbUrl, external: true }
  ];

  return (
    <header 
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        zIndex: 100,
        background: isScrolled ? 'rgba(10, 61, 98, 0.95)' : 'transparent',
        backdropFilter: isScrolled ? 'blur(10px)' : 'none',
        transition: 'all 0.3s ease',
        boxShadow: isScrolled ? '0 4px 20px rgba(0, 0, 0, 0.1)' : 'none'
      }}
    >
      <nav className="container-coastal">
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: 'var(--spacing-md) 0'
        }}>
          {/* Logo */}
          <a 
            href="#hero" 
            style={{ 
              textDecoration: 'none',
              color: 'white',
              fontFamily: 'var(--font-display)',
              fontSize: '1.5rem',
              fontWeight: '700'
            }}
          >
            Acadia Schoodic
          </a>

          {/* Desktop Navigation */}
          <div style={{ 
            display: 'flex', 
            gap: 'var(--spacing-lg)',
            alignItems: 'center'
          }} className="desktop-nav">
            {navLinks.map((link, index) => (
              <a
                key={index}
                href={link.href}
                target={link.external ? '_blank' : undefined}
                rel={link.external ? 'noopener noreferrer' : undefined}
                className="nav-link"
                style={{
                  display: link.name === 'Book Now' ? 'none' : 'inline-block'
                }}
              >
                {link.name}
              </a>
            ))}
            <a
              href={propertyData.airbnbUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-primary"
              style={{
                padding: '0.75rem 1.5rem',
                fontSize: '0.875rem',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '0.5rem'
              }}
            >
              Book Now <ExternalLink size={16} />
            </a>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            style={{
              display: 'none',
              background: 'none',
              border: 'none',
              color: 'white',
              cursor: 'pointer'
            }}
            className="mobile-menu-btn"
          >
            {isMobileMenuOpen ? <X size={28} /> : <Menu size={28} />}
          </button>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div 
            style={{
              display: 'none',
              flexDirection: 'column',
              gap: 'var(--spacing-md)',
              paddingBottom: 'var(--spacing-lg)',
              borderTop: '1px solid rgba(255, 255, 255, 0.2)',
              paddingTop: 'var(--spacing-md)'
            }}
            className="mobile-nav"
          >
            {navLinks.map((link, index) => (
              <a
                key={index}
                href={link.href}
                target={link.external ? '_blank' : undefined}
                rel={link.external ? 'noopener noreferrer' : undefined}
                className="nav-link"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                {link.name}
              </a>
            ))}
          </div>
        )}
      </nav>

      <style>{`
        @media (max-width: 768px) {
          .desktop-nav {
            display: none !important;
          }
          .mobile-menu-btn {
            display: block !important;
          }
          .mobile-nav {
            display: ${isMobileMenuOpen ? 'flex' : 'none'} !important;
          }
        }
      `}</style>
    </header>
  );
};

const Footer = () => {
  const currentYear = new Date().getFullYear();

  return (
    <footer style={{
      background: 'var(--color-dark-slate)',
      color: 'white',
      padding: 'var(--spacing-2xl) 0 var(--spacing-lg) 0'
    }}>
      <div className="container-coastal">
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
          gap: 'var(--spacing-xl)',
          marginBottom: 'var(--spacing-xl)'
        }}>
          {/* About */}
          <div>
            <h3 style={{
              fontFamily: 'var(--font-display)',
              fontSize: '1.5rem',
              marginBottom: 'var(--spacing-md)'
            }}>
              Acadia Schoodic Oceanfront Luxury
            </h3>
            <p style={{ 
              color: 'rgba(255, 255, 255, 0.7)',
              lineHeight: '1.7',
              marginBottom: 'var(--spacing-md)'
            }}>
              Your perfect oceanfront retreat on the Schoodic Peninsula of Acadia National Park. 5BR | Hot Tub | Beach Access.
            </p>
            <div style={{ display: 'flex', gap: 'var(--spacing-sm)' }}>
              <a href="#" style={{ color: 'white', transition: 'color 0.3s' }}>
                <Facebook size={24} />
              </a>
              <a href="#" style={{ color: 'white', transition: 'color 0.3s' }}>
                <Instagram size={24} />
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 style={{
              fontWeight: '600',
              marginBottom: 'var(--spacing-md)',
              fontSize: '1.125rem'
            }}>
              Quick Links
            </h4>
            <ul style={{ listStyle: 'none', padding: 0 }}>
              {['About', 'Gallery', 'Reviews', 'Location', 'Amenities'].map((link) => (
                <li key={link} style={{ marginBottom: '0.5rem' }}>
                  <a 
                    href={`#${link.toLowerCase()}`}
                    style={{
                      color: 'rgba(255, 255, 255, 0.7)',
                      textDecoration: 'none',
                      transition: 'color 0.3s'
                    }}
                  >
                    {link}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h4 style={{
              fontWeight: '600',
              marginBottom: 'var(--spacing-md)',
              fontSize: '1.125rem'
            }}>
              Contact
            </h4>
            <div style={{ 
              display: 'flex', 
              flexDirection: 'column', 
              gap: 'var(--spacing-sm)',
              color: 'rgba(255, 255, 255, 0.7)'
            }}>
              <p style={{ fontWeight: '500', color: 'rgba(255, 255, 255, 0.9)' }}>Donald (Phillip) Kelly</p>
              <a href="mailto:thirstbreakers@gmail.com" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', color: 'rgba(255, 255, 255, 0.7)', textDecoration: 'none' }}>
                <Mail size={18} />
                <span>thirstbreakers@gmail.com</span>
              </a>
              <a href="tel:+12078529447" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', color: 'rgba(255, 255, 255, 0.7)', textDecoration: 'none' }}>
                <Phone size={18} />
                <span>+1 (207) 852-9447</span>
              </a>
            </div>
          </div>

          {/* Location */}
          <div>
            <h4 style={{
              fontWeight: '600',
              marginBottom: 'var(--spacing-md)',
              fontSize: '1.125rem'
            }}>
              Location
            </h4>
            <p style={{ color: 'rgba(255, 255, 255, 0.7)', lineHeight: '1.7' }}>
              Schoodic Peninsula<br />
              Gouldsboro, Maine<br />
              Near Acadia National Park
            </p>
          </div>
        </div>

        <div style={{
          borderTop: '1px solid rgba(255, 255, 255, 0.1)',
          paddingTop: 'var(--spacing-lg)',
          textAlign: 'center',
          color: 'rgba(255, 255, 255, 0.6)',
          fontSize: '0.875rem'
        }}>
          <p>© {currentYear} Schoodic Serenity. All rights reserved. | Vacation Rental in Maine</p>
        </div>
      </div>
    </footer>
  );
};

export { Header, Footer };
